import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type RefObject,
} from "react";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import {
  canonicalAttachmentUrl,
  resolveAttachmentMedia,
  uploadAssetPath,
} from "@/lib/attachment-media";
import { htmlAssetUrls } from "./html-upload-assets";
import { htmlEditBase } from "./html-frame-edits";
import {
  HTML_ASSETS_READY,
  HTML_ASSETS_UPDATE,
  privateHtmlAssetScript,
} from "./html-private-assets";

/** A document-bound port prevents delivery to a navigated WindowProxy. */
export function useHtmlPrivateAssets(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  html: string,
  enabled: boolean,
) {
  const localReferences = useRef(new Map<string, string>());
  const scope = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    getDesktopChatAuthScope,
  );
  const token = useMemo(
    () => `${htmlEditBase(html)}:${scope?.epoch ?? 0}:${crypto.randomUUID()}`,
    [html, scope],
  );
  const urls = useMemo(
    () => htmlAssetUrls(html).filter((url) => uploadAssetPath(url)),
    [html],
  );
  useEffect(() => {
    const references = localReferences.current;
    references.clear();
    if (!enabled || urls.length === 0) return;
    let active = true;
    let port: MessagePort | undefined;
    let generation = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let handshakeTimer: ReturnType<typeof setTimeout> | undefined;
    const resolve = async (
      target: MessagePort,
      version: number,
      refresh = false,
    ) => {
      const results = await Promise.allSettled(
        urls.map(async (source) => ({
          source,
          ...(await resolveAttachmentMedia(source, { refresh })),
        })),
      );
      if (!active || version !== generation || port !== target) return;
      const resolved = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );
      for (const item of resolved)
        if (/^(data|blob):/i.test(item.url))
          references.set(item.url, canonicalAttachmentUrl(item.source));
      target.postMessage({
        type: HTML_ASSETS_UPDATE,
        urls: Object.fromEntries(
          resolved.map((item) => [item.source, item.url]),
        ),
      });
      const expiresAt = Math.min(...resolved.map((item) => item.expiresAt));
      const failed = results.some((result) => result.status === "rejected");
      if (Number.isFinite(expiresAt) || failed) {
        clearTimeout(timer);
        timer = setTimeout(
          () => void resolve(target, version, true),
          Math.max(1000, failed ? 30_000 : expiresAt - Date.now() - 30_000),
        );
      }
    };
    const iframe = iframeRef.current;
    const request = () => {
      clearTimeout(timer);
      clearTimeout(handshakeTimer);
      port?.close();
      references.clear();
      const version = ++generation;
      const channel = new MessageChannel();
      port = channel.port1;
      const target = port;
      target.onmessage = (event) => {
        if (
          !active ||
          version !== generation ||
          event.data?.type !== HTML_ASSETS_READY ||
          event.data?.token !== token
        )
          return;
        clearTimeout(handshakeTimer);
        target.onmessage = null;
        void resolve(target, version);
      };
      target.start();
      // No nonce or image bytes are sent to an unverified document.
      iframe?.contentWindow?.postMessage(
        { type: "denker:private-assets-request" },
        "*",
        [channel.port2],
      );
      handshakeTimer = setTimeout(() => target.close(), 10_000);
    };
    const onMessage = (event: MessageEvent) => {
      if (
        event.source === iframe?.contentWindow &&
        event.data?.type === "denker:private-assets-listening"
      )
        request();
    };
    window.addEventListener("message", onMessage);
    iframe?.addEventListener("load", request);
    request();
    return () => {
      active = false;
      references.clear();
      clearTimeout(timer);
      clearTimeout(handshakeTimer);
      port?.close();
      window.removeEventListener("message", onMessage);
      iframe?.removeEventListener("load", request);
    };
  }, [enabled, iframeRef, scope, token, urls]);
  const canonicalize = useCallback(
    (value: string) =>
      localReferences.current.get(value) ?? canonicalAttachmentUrl(value),
    [],
  );
  // Backend history may contain signed references inside authored scripts or
  // text too. Those documents require the same restriction even without an img.
  const privateDocument =
    html.includes("/api/v1/uploads/") || html.includes("/media/uploads/");
  return {
    script: enabled && privateDocument ? privateHtmlAssetScript(token) : "",
    canonicalize,
  };
}
